import { Eye } from "lucide-react";
import styled from "styled-components";

import {
  Table as UITable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TableLoad } from "@/components/ui/table-load";
import { EmptyTable } from "@/components/Datatable/components/EmptyTable";
import { Button } from "components/ui/button";
import { formatAndConvertTimezoneDateTime } from "@/utils/format-date";
import { formatToBRL } from "@/utils/formatToMoney";
import { getPagesToShow } from "@/utils/pagination";
import type { Meta } from "src/@types/paginated";

import type { IDepositBatchRow } from "src/@types/deposit-reconciliation";
import { BatchStatusBadge } from "./status-badge";
import PageSizeSelect from "../../../components/page-size-select";
import { TEXTS } from "../../../utils/messages";

interface DepositBatchesTableProps {
  data: IDepositBatchRow[];
  page: number;
  pageSize: number;
  total: number;
  isFetching: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onViewDetails: (batchId: number) => void;
}

export default function DepositBatchesTable({
  data,
  page,
  pageSize,
  total,
  isFetching,
  onPageChange,
  onPageSizeChange,
  onViewDetails,
}: DepositBatchesTableProps) {
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const meta: Meta = {
    currentPage: page,
    lastPage,
    perPage: pageSize,
    total,
    prev: page > 1 ? page - 1 : null,
    next: page < lastPage ? page + 1 : null,
  };

  return (
    <div className="border border-slate-200 rounded-md bg-white">
      <UITable>
        <TableHeader>
          <TableRow>
            <TableHead className="text-xs">ID</TableHead>
            <TableHead className="text-xs">Loja</TableHead>
            <TableHead className="text-xs">{TEXTS.TABLE.CONCILIATED_BY}</TableHead>
            <TableHead className="text-xs">{TEXTS.TABLE.CONCILIATED_AT}</TableHead>
            <TableHead className="text-xs text-center">NSUs</TableHead>
            <TableHead className="text-xs text-center">Depósitos</TableHead>
            <TableHead className="text-xs text-center">Valor total</TableHead>
            <TableHead className="text-xs text-center">Status</TableHead>
            <TableHead className="text-xs text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isFetching ? (
            <TableLoad colSpan={9} />
          ) : data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} className="text-center h-24">
                <EmptyTable text="Nenhuma conciliação encontrada no período." />
              </TableCell>
            </TableRow>
          ) : (
            data.map(b => (
              <TableRow key={b.id} className="hover:bg-slate-50">
                <TableCell className="font-mono text-xs">#{b.id}</TableCell>
                <TableCell className="text-xs">Loja {b.storeCode}</TableCell>
                <TableCell className="text-xs">{b.createdByName ?? b.createdByEmail}</TableCell>
                <TableCell className="text-xs">{formatAndConvertTimezoneDateTime(b.committedAt ?? b.createdAt)}</TableCell>
                <TableCell className="text-xs text-right">{b.nsuCount}</TableCell>
                <TableCell className="text-xs text-right">{b.depositCount}</TableCell>
                <TableCell className="text-xs text-right font-medium">
                  {formatToBRL(b.totalAmount)}
                </TableCell>
                <TableCell>
                  <div className="flex justify-center">
                    <BatchStatusBadge status={b.status} />
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-[#2B0C1A] hover:bg-[#2B0C1A]/10 hover:text-[#2B0C1A]"
                    onClick={() => onViewDetails(b.id)}
                  >
                    <Eye className="w-3.5 h-3.5 mr-1" />
                    Detalhes
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </UITable>

      <div className="flex w-full items-center justify-between px-3 py-2 text-xs border-t border-slate-200">
        <PaginationButtonsContainer>
          {data.length !== 0 && (
            <>
              <button
                className={`navigate-btn previous ${
                  meta.prev !== null && !isFetching ? "" : "disabled"
                }`}
                onClick={() => meta.prev !== null && onPageChange(meta.prev)}
                disabled={meta.prev === null || isFetching}
              >
                Anterior
              </button>
              {getPagesToShow(meta).map((p, idx) => (
                <span
                  key={`${p}-${idx}`}
                  className={`pages ${p === meta.currentPage ? "selected" : ""} ${
                    p === "..." ? "ocult" : ""
                  }`}
                  onClick={() => typeof p === "number" && onPageChange(p)}
                >
                  {p}
                </span>
              ))}
              <button
                className={`navigate-btn next ${
                  meta.next !== null && !isFetching ? "" : "disabled"
                }`}
                onClick={() => meta.next !== null && onPageChange(meta.next)}
                disabled={meta.next === null || isFetching}
              >
                Próximo
              </button>
            </>
          )}
        </PaginationButtonsContainer>
        <div className="flex items-center gap-4">
          <PageSizeSelect value={pageSize} onChange={onPageSizeChange} />
          <span className="text-slate-600">
            Página {page} de {lastPage} ({total} {total === 1 ? "conciliação" : "conciliações"})
          </span>
        </div>
      </div>
    </div>
  );
}

const PaginationButtonsContainer = styled.div`
  display: flex;
  font-size: 14px;

  .pages {
    display: flex;
    justify-content: center;
    padding: 8px 6px;
    border: 1px solid #dcdce3;
    border-left: none;
    border-top: none;
    min-width: 29px;

    &.selected {
      background-color: #f4c83e;
    }
  }

  .pages:not(.ocult) {
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .navigate-btn {
    cursor: pointer;
    padding: 6px 12px;
    background-color: #2b0c1a;
    color: #fff;

    &.previous {
      border-radius: 0 0 0 8px;
    }

    &.next {
      border-radius: 0 0 8px 0;
    }

    &:hover {
      text-decoration: underline;
    }

    &.disabled {
      color: #8d91a2;
      background-color: #d9dae0;
      cursor: not-allowed;
      text-decoration: none;
    }
  }
`;
